import {
    GameMasterCampaignBuilderGameApi,
    GameMasterWorldHierarchyGameApi,
    GMWorldSubLocationsApi,
    GMWorldPositionsApi,
    GMWorldPositionConnectionsApi,
    TacticalMapTemplatesGameApi,
} from '@/api/backendService.js';
import {createCreationMethods} from './creation.js';
import {createBuilderMethods} from './builder.js';
import {createMapMethods} from './map.js';
import {createNpcMethods} from './npc.js';
import {createObjectivesMethods} from './objectives.js';
import {createParticipantsMethods} from './participants.js';
import {createSummaryMethods} from './summary.js';
import {createPublishMethods} from './publish.js';

/**
 * Campaign Builder Service
 *
 * Thin wrapper around the generated GameMasterCampaignBuilderApi client. The
 * client is injected so tests can pass a bare `{method: vi.fn()}` fake instead
 * of the real axios-backed singleton — the same idiom as `transitionService.js`
 * and `WorldMapEditorService.js`.
 *
 * Every method returns the unwrapped `response.data`; callers never touch an
 * axios response object.
 *
 * The map step additionally needs the shared SANDBOX world hierarchy (city
 * through sub-location) and the tactical map template catalogue, so this
 * service also wraps `GameMasterWorldHierarchyApi`, the world-editor
 * sub-location/position/connection endpoints (composed rather than
 * duplicated — `WorldMapEditorService` owns their CRUD idiom) and
 * `TacticalMapTemplatesApi`. Every one of these injectable clients defaults
 * to the real singleton and is overridden only in tests.
 *
 * Methods are grouped by step into sibling modules (`creation`, `builder`,
 * `map`, `npc`, `objectives`, `participants`, `summary`, `publish`) and
 * composed here onto a single instance so the public surface — one service,
 * one set of method names — never changes.
 */
export class CampaignBuilderService {
    constructor(
        api = GameMasterCampaignBuilderGameApi,
        worldApi = GameMasterWorldHierarchyGameApi,
        subLocationsApi = GMWorldSubLocationsApi,
        positionsApi = GMWorldPositionsApi,
        connectionsApi = GMWorldPositionConnectionsApi,
        templatesApi = TacticalMapTemplatesGameApi,
    ) {
        this.api = api;
        this.worldApi = worldApi;
        this.subLocationsApi = subLocationsApi;
        this.positionsApi = positionsApi;
        this.connectionsApi = connectionsApi;
        this.templatesApi = templatesApi;

        Object.assign(
            this,
            createCreationMethods(api),
            createBuilderMethods(api),
            createMapMethods({api, worldApi, subLocationsApi, positionsApi, connectionsApi, templatesApi}),
            createNpcMethods(api),
            createObjectivesMethods(api),
            createParticipantsMethods(api),
            createSummaryMethods(api),
            createPublishMethods(api),
        );
    }
}

export default new CampaignBuilderService();
